import { FC, useMemo } from 'react';

import { BsArrowRight } from 'react-icons/bs';
import { Link, useNavigate } from 'react-router-dom';
import { isEmpty, lowerCase } from 'lodash';
import { BiLinkExternal } from 'react-icons/bi';
import {
  cn,
  compareObjects,
  convertCamelCaseToWords,
  convertToAmount,
  useAdminParams,
} from 'utils/helpers';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { User } from 'utils/types';
import c from 'utils/constants';
import Cookies from 'js-cookie';

import { LOG_ACTOR } from './constants';
import Button from '../../Button';
import {
  CompanyLogs as COMPANIES,
  LeadLogs as LEADS,
  DealLogs as DEALS,
} from './logs';

export const ActivityLogDataDetails: FC<{
  disabled: boolean;
  link: string;
}> = ({ disabled, link }) => {
  const navigate = useNavigate();

  return (
    <Button
      className={cn(
        'text-xs',
        'font-semibold',
        'p-0',
        !disabled ? 'text-secondary-base' : 'text-disabled',
        disabled ? 'cursor-not-allowed' : '',
      )}
      intent="link"
      onClick={() => navigate(link)}
      disabled={disabled}
      label="VIEW DETAILS"
      icon={() => <BiLinkExternal />}
      right={true}
    />
  );
};

export const ActivityLogData: FC<{ data: any; prevData: any }> = ({
  data,
  prevData,
}) => {
  const entries = useMemo(() => {
    return Object.entries(compareObjects(prevData, data)).map(
      ([field, value]) => {
        if (typeof value?.old === 'number' || typeof value?.new === 'number')
          return [
            field,
            {
              old: `$${convertToAmount(value.old)}`,
              new: `$${convertToAmount(value.new)}`,
            },
          ];

        return [field, value];
      },
    );
  }, [prevData, data]);

  return (
    <div>
      {entries.map(([field, value]) => {
        return (
          <div key={Math.random()} className="flex items-center space-x-1">
            <div className="font-semibold">
              {convertCamelCaseToWords(field)}:
            </div>
            <div className="text-xs">
              {!isEmpty(value.old) ? (
                value.old
              ) : (
                <span className="italic text-disabled">
                  {lowerCase(`no ${convertCamelCaseToWords(field)}`)}
                </span>
              )}
            </div>
            <BsArrowRight className="text-secondary-dark" />
            <div className="text-xs">
              {!isEmpty(value.new) ? (
                value.new
              ) : (
                <span className="italic text-disabled">
                  {lowerCase(`no ${convertCamelCaseToWords(field)}`)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ActivityLogDataArray: FC<{
  data: User[];
  link: string;
  disabled: boolean;
}> = ({ data, disabled, link }) => {
  const users = useMemo(() => {
    if (data.length === 1) return data;

    const _users: any[] = [];

    data.every((value, key) => {
      if (data.length === key + 2) {
        _users.push(value);
        _users.push(' and ');

        return true;
      }

      if (data.length > key) {
        _users.push(value);

        if (data.length !== key + 1) _users.push(', ');

        return true;
      }

      _users.push(value);
      _users.push(' ');
    });

    return _users;
  }, [data]);

  return (
    <>
      {users.map((user) => {
        if (typeof user === 'string') return user;

        return (
          <Link
            key={Math.random()}
            className={cn(
              'text-sm',
              'font-semibold',
              'p-0',
              !disabled ? 'text-info-base' : 'text-disabled',
              disabled ? 'cursor-not-allowed' : '',
            )}
            to={`${link}${user?._id}`}
            onClick={(e) => {
              if (disabled) e.preventDefault();
            }}
          >
            {user?.fullName ?? user?.name}
          </Link>
        );
      })}
    </>
  );
};

const { ACCESS_LEVEL, PERMISSION_KEYS } = c;

export const Actor: FC<{ data: LOG_ACTOR }> = ({ data }) => {
  const role = Cookies.get('_role');
  const { accountId, accountUserId } = useAdminParams();
  const canViewLeadsAll = useCanRolePermissions(
    ACCESS_LEVEL.ACCOUNT_ADMIN,
    PERMISSION_KEYS.LEADS,
  );

  const link = useMemo(() => {
    if (!data?.role) {
      if (role === 'account')
        return `/settings/teams/people?id=${data?.actorId}`;

      // eslint-disable-next-line max-len
      return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${data?.actorId}`;
    }

    if (data?.role === 'account')
      return `/settings/teams/people?id=${data?.actorId}`;

    // eslint-disable-next-line max-len
    return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${data?.actorId}`;
  }, [data, role, accountId, accountUserId]);

  if (!data?.role) return data?.source;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const disabled: boolean = useMemo(() => {
    if (data?.role === 'admin') return true;

    return !canViewLeadsAll;
  }, [data, canViewLeadsAll]);

  return (
    <Link
      className={cn(
        'text-sm',
        'font-semibold',
        'p-0',
        !disabled ? 'text-info-base' : 'text-disabled',
        disabled ? 'cursor-not-allowed' : '',
      )}
      to={link}
      onClick={(e) => {
        if (disabled) e.preventDefault();
      }}
    >
      {data?.meta?.fullName}
    </Link>
  );
};

const ActivityLog = {
  LEADS: { ...LEADS },
  COMPANIES: { ...COMPANIES },
  DEALS: { ...DEALS },
} as any;

export default ActivityLog;
